import { useEffect } from "react";
import { useRouter } from "next/router";
import { useSchema } from "../contexts/schema.context";
import { SchemaRegistry } from "src/services/schema/registry/schema.registry";
import { OrganizationData } from "src/services/schema/builders/organization.builder";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { locale, storeKey, storeConfig } from "src/config/app-config";
import { getBaseUrl, toAbsoluteUrl } from "src/utils/getBaseUrl";
import { checkoutCOMCountryOptions } from "src/utils/regionName/countries";

/**
 * Custom hook for managing Organization schema on the Women homepage
 * Only adds the schema when on the Women homepage route (/women)
 */
export const useOrganizationSchema = (description: string): void => {
  const { addSchemas, removeSchemas } = useSchema();
  const router = useRouter();

  useEffect(() => {
    // Only show on /women route
    const localeGenderId = localeFix({ gender: router.query.slug as string });

    const schemaId = "organization-homepage";

    if (localeGenderId !== "women") {
      removeSchemas(schemaId);
      return;
    }

    // Organization schema data
    const organizationData: OrganizationData = {
      name: "Otrium",
      url: getBaseUrl(locale as string),
      logo: toAbsoluteUrl("/static/favicon.png") || "",
      description,
      foundingDate: "2016",
      contactPoint: {
        telephone: "+31-20-221-7184",
        contactType: "Customer Service",
        availableLanguage:
          storeKey === "OT-COM"
            ? ["nl-NL", "en-GB", "de-DE"]
            : [storeConfig.locale, "en-GB"],
        areaServed:
          storeKey === "OT-COM"
            ? checkoutCOMCountryOptions.map((country) => country.value)
            : [storeConfig.countryCode || "NL"],
      },
      sameAs: [
        "https://www.linkedin.com/company/otrium",
        "https://www.instagram.com/otriumofficial/",
        "https://www.facebook.com/otriumofficial/",
        "https://www.tiktok.com/@otrium",
      ],
    };

    // Generate schemas using the registry
    const schemas = SchemaRegistry.organization(organizationData);

    addSchemas(schemas, schemaId);

    // Cleanup on unmount
    return () => removeSchemas(schemaId);
  }, [router.pathname, router.asPath, addSchemas, removeSchemas]);
};
